import { redirect, useRouter } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { api, ApiRequestError } from '@/lib/api'
import { errorMessage } from '@/components/app/states'

/** Only same-app ops paths are accepted as a post-login target (prevents open redirects). */
export function safeOpsPath(target: string | undefined): string {
  return target && target.startsWith('/ops') && !target.startsWith('//') && !target.startsWith('/ops/login') ? target : '/ops'
}

function currentPath(): string {
  return window.location.pathname + window.location.search
}

/**
 * `api()` for loaders and beforeLoad under /ops. A 401 becomes a router redirect to /ops/login
 * that returns to `from` (defaults to the current URL) after sign-in.
 */
export async function opsApi<T>(path: string, init?: RequestInit & { json?: unknown }, from?: string): Promise<T> {
  try {
    return await api<T>(path, init)
  } catch (e) {
    if (e instanceof ApiRequestError && e.status === 401) {
      throw redirect({ to: '/ops/login', search: { redirect: safeOpsPath(from ?? currentPath()) } })
    }
    throw e
  }
}

/**
 * Runs an ops mutation from an event handler. Tracks pending and error state,
 * sends the user to /ops/login on 401, and reloads route data on success.
 */
export function useOpsMutation() {
  const router = useRouter()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = useCallback(
    async <T,>(fn: () => Promise<T>, opts: { invalidate?: boolean } = {}): Promise<T | undefined> => {
      setPending(true)
      setError(null)
      try {
        const result = await fn()
        if (opts.invalidate !== false) await router.invalidate()
        return result
      } catch (e) {
        if (e instanceof ApiRequestError && e.status === 401) {
          await router.navigate({ to: '/ops/login', search: { redirect: safeOpsPath(currentPath()) } })
          return undefined
        }
        setError(errorMessage(e))
        return undefined
      } finally {
        setPending(false)
      }
    },
    [router],
  )

  return { run, pending, error, setError }
}
